import { mapTargetCTA, mapTargetLink, mapTargetNonRepetitiveLink } from '@/App.datalayer';
import ContentContainer from '@/features/shared/ContentContainer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import {
  CARD_CAROUSEL_EVENTS,
  CARD_CAROUSEL_EVENTS_NAMES,
  LIST_CARD_CAROUSEL_EVENTS,
  LIST_CARD_CAROUSEL_EVENTS_NAMES,
  onDataLayerCardCarousel,
} from '@/features/shared/portal/PortalCarousel/PortalCarousel.datalayer';
import { MaxWidth } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import { CardCarouselProps } from './CardCarousel.interface';
import useStyles from './CardCarousel.styles';
import ContentCardCarousel from './variants/ContentCardCarousel';
import LogoCardCarousel from './variants/LogoCard';

const MappedContentCardGTMCarouselLink = withGTM({
  Component: ContentCardCarousel,
  onDatalayer: onDataLayerCardCarousel({}),
  config: {
    mapTarget: mapTargetNonRepetitiveLink,
  },
});
const MappedContentCardGTMCarouselCTA = withGTM({
  Component: MappedContentCardGTMCarouselLink,
  onDatalayer: onDataLayerCardCarousel({
    event: CARD_CAROUSEL_EVENTS.ctaClick,
    eventName: CARD_CAROUSEL_EVENTS_NAMES.ctaClick,
  }),
  config: { mapTarget: mapTargetCTA },
});

const MappedLogoCardCarouselGTMCarouselLink = withGTM({
  Component: LogoCardCarousel,
  onDatalayer: onDataLayerCardCarousel({
    event: LIST_CARD_CAROUSEL_EVENTS.cardClick,
    eventName: LIST_CARD_CAROUSEL_EVENTS_NAMES.cardClick,
  }),
  config: { mapTarget: mapTargetLink },
});
const MappedLogoCardCarouselGTMCarouselCTA = withGTM({
  Component: MappedLogoCardCarouselGTMCarouselLink,
  onDatalayer: onDataLayerCardCarousel({
    event: LIST_CARD_CAROUSEL_EVENTS.ctaClick,
    eventName: LIST_CARD_CAROUSEL_EVENTS_NAMES.ctaClick,
  }),
  config: { mapTarget: mapTargetCTA },
});

const mapTemplateToComponent = (template: string) => {
  if (/^logo/i.test(template)) {
    return MappedLogoCardCarouselGTMCarouselCTA;
  }

  return MappedContentCardGTMCarouselCTA;
};

const CardCarousel = ({
  content,
  presentation,
  contentButtons,
}: Br.PropsWithContentButtons<CardCarouselProps>) => {
  const {
    template,
    cardColor,
    containerTopSpacing,
    containerBottomSpacing,
    containerTopBackgroundColor,
    containerBottomBackgroundColor,
    containerTopBackgroundImage,
    hasTextShadow,
    backgroundOverlayOpacity,
    hideCTA,
    hideNavigation,
    contentAlignment,
    hasDropShadow,
    autoscroll,
    autoscrollSpeed,
    autoscrollDirection,
    mobileStacked,
  } = presentation;
  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: containerBottomBackgroundColor }),
    [containerBottomBackgroundColor],
  );
  const cardTheme = useMemo(() => resolveTheme('bg-color')({ rawVal: cardColor }), [cardColor]);
  const classes = useStyles({});
  const Variant = useMemo(() => mapTemplateToComponent(template), [template]);

  const containerProps = useMemo(
    () => ({
      topBackgroundColor: containerTopBackgroundColor,
      bottomBackgroundColor: containerBottomBackgroundColor,
      topSpacing: containerTopSpacing,
      bottomSpacing: containerBottomSpacing,
      backgroundImage: containerTopBackgroundImage,
      mobileBackgroundImage: containerTopBackgroundImage,
      overlay: {
        hasTextShadow,
        opacity: backgroundOverlayOpacity,
        alignment: 'bottom',
        maxWidth: MaxWidth.large,
      },
    }),
    [
      backgroundOverlayOpacity,
      containerBottomBackgroundColor,
      containerBottomSpacing,
      containerTopBackgroundColor,
      containerTopBackgroundImage,
      containerTopSpacing,
      hasTextShadow,
    ],
  );

  const containerClassName = clsx(
    classes.container,
    contentAlignment?.length && `content-align-${contentAlignment}`,
    !hasDropShadow && 'no-drop-shadow',
  );

  const variantProps = {
    theme,
    cardTheme,
    cardColor,
    hideCTA,
    hideNavigation,
    autoscroll,
    autoscrollSpeed,
    autoscrollDirection,
    mobileStacked,
  };

  const wrapperClassName = clsx(classes.wrapper, `${theme}-theme`);

  return (
    <ContentContainer {...containerProps}>
      <div className={wrapperClassName}>
        <MuiContainer className={containerClassName}>
          <Variant content={content} {...variantProps} contentButtons={contentButtons} />
        </MuiContainer>
      </div>
    </ContentContainer>
  );
};

export default memo(CardCarousel);
